const germanDateRegEx = new RegExp('((0[1-9]|[12]\\d|3[01]).(0[1-9]|1[0-2]).[12]\\d{3})');
const iso8601DateRegEx = new RegExp('([12]\\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01]))');
const DAY_IN_MILLISECONDS = 60 * 60 * 24 * 1000;

export const splitGermanDateString = (dateString: string) => {
    if (!germanDateRegEx.test(dateString)) {
        throw new Error(`"${dateString}" does not have format DD.MM.YYYY`);
    }

    return dateString.split('.').map((val) => parseInt(val, 10));
};

export const splitIso8601DateString = (dateString: string) => {
    if (!iso8601DateRegEx.test(dateString)) {
        throw new Error(`"${dateString}" does not have format YYYY-MM-DD`);
    }

    return dateString.split('-').map((val) => parseInt(val, 10));
};

/**
 * transform german date string to UTC timestamp
 * @param dateString "DD.MM.YYYY"
 */
export const germanDateStringToTimestamp = (dateString: string) => {
    try {
        const [DD, MM, YYYY] = splitGermanDateString(dateString);
        return Date.UTC(YYYY, MM - 1, DD);
    } catch (error) {
        throw new Error(`Unable to convert string "${dateString}" to timestamp`);
    }
};

/**
 * transform ISO 8601 date string to UTC timestamp
 * @param dateString "YYYY-MM-DD"
 */
export const iso8601DateStringToTimestamp = (dateString: string) => {
    try {
        const [YYYY, MM, DD] = splitIso8601DateString(dateString);
        return Date.UTC(YYYY, MM - 1, DD);
    } catch (error) {
        throw new Error(`Unable to convert string "${dateString}" to timestamp`);
    }
};

export const getAgeByDate = (dateOfBirth: Date) => {
    const now = new Date();
    const m = now.getUTCMonth() - dateOfBirth.getUTCMonth();
    const age = now.getUTCFullYear() - dateOfBirth.getUTCFullYear();

    if (m < 0 || (m === 0 && now.getUTCDate() < dateOfBirth.getUTCDate())) {
        return age - 1;
    }
    return age;
};

export const getAgeByGermanDateString = (dateString: string) => {
    try {
        const timestamp = germanDateStringToTimestamp(dateString);
        const date = new Date(timestamp);
        return getAgeByDate(date);
    } catch {
        throw new Error(`Unable to calculate age of birthdate "${dateString}"`);
    }
};

export const getFormattedNextDay = (dateString: string) => {
    try {
        const [day, month, year] = splitGermanDateString(dateString);
        const startDate = new Date(year, month - 1, day);
        const nextDay = new Date(startDate.getTime() + DAY_IN_MILLISECONDS);

        return (
            nextDay
                .toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                // IE displays weird questionmarks in the date string.
                // Those are U+200E 'LEFT-TO-RIGHT MARK' unicode chars.
                // To avoid this bug we need to replace all occurrences of those LRMs
                .replace(/\u200E/g, '')
        );
    } catch (error) {
        throw new Error(`Unable to parse formatted date string "${dateString}"`);
    }
};
